import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { FG_WAREHOUSE_ID, packPer } from './wms-fg.seed'
import { type ClmActionName, type ClmActorCode, type ClmStatusCode, WF_CLM } from './workflow-clm.seed'

/**
 * Customer Claim FG (C11, v7.50 — laporan 23 §2.3 / §4, C-13): seed DITULIS ULANG ke status baru (tanpa migrasi; versi
 * seed naik). Tabel: `claim`, `claim_item` (satu baris per label yang discan + keputusan per unit), `claim_sort_task`
 * (nomor `SRT-`, `operator_parts`), `claim_store_task` (HANYA claim lama sebelum v7.50), `fg_putaway_task(+_item)` sumber
 * CLAIM untuk claim yang Diproses, `fg_ng_pack` Menunggu Disimpan untuk Not Good yang belum dibawa ke karantina.
 *
 * Isi (angka prototype v7.50 `jal-seed.js` Phase 10 untuk lima claim lama):
 * - CLM-260818-003 / -002 / -001 Disetujui (claim lama: dibuka, Good 601 / 788 / 790, Not Good sudah di ZN-01-P1 dan
 *   terjadwal di DSP-260818-011) → demo Proses Claim (Good pack saja).
 * - CLM-260816-004 Diproses lewat tugas simpan LAMA SPC-260815-004 (Good 776 → RC-P1, Budi, belum selesai) → layar simpan
 *   Claim — Operator tetap terbuka untuk claim lama; NG 24 di antrean pemusnahan.
 * - CLM-260816-005 Selesai (tugas simpan lama selesai).
 * - CLM-260818-006 Sortir Berjalan SRT-260818-011 (Budi): 2 Karton + 1 Slop TBC-ONE-12 sudah discan, belum diputuskan.
 * - CLM-260818-007 Menunggu Approval (Budi: 1 Karton Bagus), diajukan 3 menit sebelum seed dibangun → hitung mundur
 *   15 menit; demo auto-approve dengan SLA 1 menit.
 * - CLM-260818-008 Diproses (alur v7.50): 1 Bal dibuka → Good 198 / Repack 1 / Not Good 1; tugas putaway FPW-260818-031
 *   (Budi) dengan Repack terkunci ke RC-P2 dan Not Good ke ZN-01-P1.
 * Label yang sengaja dibiarkan bebas untuk demo scan: 001/SJ/08 TBC-ONE-12 Karton, 003/SJ/09 TBC-BOLD-12 Karton #2,
 * 003/SJ/09 MILLIONS Karton, 004/SJ/08 TBC-ONE-12 Slop #2.
 *
 * Dipasang SETELAH seed outbound (`applyClaimData`) karena memakai `fg_label` SHIPPED. Kolom di luar ERD (laporan 15, 29):
 * `claim.{sort_locked_at, d2_decided_by_id, d2_decided_at, d2_auto, return_reason, resort_count, processed_at,
 * processed_by_id, is_completed, completed_at}`, `claim_item.{sort_result, qty_good, qty_repack, qty_ng, repack_reason_id,
 * sj_no, shipped_at, order_no, customer_id, scanned_by_id, sorted_at, sorted_by_id}`, `claim_sort_task.{operator_parts,
 * task_status, locked_at}`, `claim_store_task.{source, from_pallet_id, ng_pack_ids, task_status, stored_packs, done_at}`.
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()
const audit = (userId: string | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})
const ulil = USER_IDS.ulil
const hendra = USER_IDS.hendra

/* ------------------------------------------------------------ area & pallet claim */

export const CLAIM_ZONES = [
  ['RC', 'Zone Retur Claim', 'RETURN_CLAIM'],
  ['ZN-01', 'Area NG FG', 'NG_FG']
] as const
export const CLAIM_PALLETS = [
  ...[1, 2, 3, 4].map(n => ['RC', `RC-P${n}`] as const),
  ...[1, 2, 3, 4].map(n => ['ZN-01', `ZN-01-P${n}`] as const)
]
export const claimZoneId = (code: string) =>
  seedId(SEED_GROUP.warehouseZone, 170 + CLAIM_ZONES.findIndex(([c]) => c === code))
export const claimPalletId = (code: string) =>
  seedId(SEED_GROUP.pallet, 1100 + CLAIM_PALLETS.findIndex(([, c]) => c === code))

/* ------------------------------------------------------------ alasan (master alasan NG FG, `mt_reject_type`) */

export const CLAIM_REASONS = {
  FG_CLAIM: ['Penyok', 'Kemasan sobek — wet', 'Kemasan sobek — jelek', 'Rusak', 'Pita cukai rusak', 'Kedaluwarsa'],
  FG_REPACK: ['Kemasan kotor', 'Karton robek', 'Label rusak'],
  FG_NG: ['Penyok', 'Kemasan sobek — wet', 'Kemasan sobek — jelek', 'Basah / berjamur', 'Pita cukai rusak']
} as const
type ReasonCategory = keyof typeof CLAIM_REASONS
const REASON_LIST = (Object.keys(CLAIM_REASONS) as ReasonCategory[]).flatMap(category =>
  CLAIM_REASONS[category].map(name => [category, name] as const)
)
export const claimReasonId = (category: ReasonCategory, name: string) =>
  seedId(SEED_GROUP.rejectType, 100 + REASON_LIST.findIndex(([c, n]) => c === category && n === name))

/* ------------------------------------------------------------ spesifikasi claim */

type Pick = {
  sj: string
  sku: string
  unit: 'Karton' | 'Bal' | 'Slop'
  /** Indeks label (urut id) dalam kelompok SJ + SKU + kemasan. */
  n: number
  /** 'GOOD' = Bagus (utuh); [good, repack, ng] = dibuka; tanpa nilai = belum diputuskan. */
  decision?: 'GOOD' | [number, number, number]
  repackReason?: string
  ngReason?: string
}

type ClaimSpec = {
  no: string
  srt: string
  status: ClmStatusCode
  createdAt: string
  operator: 'budi' | 'sari'
  picks: Pick[]
  /** Menit sebelum seed dibangun saat hasil sortir diajukan (claim Menunggu Approval → hitung mundur hidup). */
  agoMin?: number
  /** Tugas simpan alur lama (claim sebelum v7.50). */
  store?: { spc: string; qty: number; done: boolean }
  /** Tugas putaway FG sumber CLAIM (alur v7.50). */
  putaway?: { fpw: string; ngNo: string }
}

const CLAIMS: ClaimSpec[] = [
  {
    no: 'CLM-260818-003',
    srt: 'SRT-260818-003',
    status: 'CLM_SORT_APPROVED',
    createdAt: '2026-08-18 08:00',
    operator: 'budi',
    picks: [
      { sj: '001/SJ/08/2026', sku: 'TBC-BOLD-20', unit: 'Karton', n: 0, decision: [601, 0, 199], ngReason: 'Penyok' }
    ]
  },
  {
    no: 'CLM-260818-002',
    srt: 'SRT-260818-002',
    status: 'CLM_SORT_APPROVED',
    createdAt: '2026-08-18 08:10',
    operator: 'budi',
    picks: [
      {
        sj: '001/SJ/08/2026',
        sku: 'TBC-BOLD-20',
        unit: 'Karton',
        n: 1,
        decision: [788, 0, 12],
        ngReason: 'Kemasan sobek — wet'
      }
    ]
  },
  {
    no: 'CLM-260818-001',
    srt: 'SRT-260818-001',
    status: 'CLM_SORT_APPROVED',
    createdAt: '2026-08-18 08:20',
    operator: 'budi',
    picks: [
      {
        sj: '002/SJ/08/2026',
        sku: 'TBC-ONE-12',
        unit: 'Karton',
        n: 0,
        decision: [790, 0, 10],
        ngReason: 'Kemasan sobek — jelek'
      }
    ]
  },
  {
    no: 'CLM-260816-004',
    srt: 'SRT-260815-004',
    status: 'CLM_PROCESSING',
    createdAt: '2026-08-15 09:00',
    operator: 'budi',
    picks: [
      {
        sj: '003/SJ/09/2026',
        sku: 'TBC-BOLD-12',
        unit: 'Karton',
        n: 0,
        decision: [776, 0, 24],
        ngReason: 'Kemasan sobek — wet'
      }
    ],
    store: { spc: 'SPC-260815-004', qty: 776, done: false }
  },
  {
    no: 'CLM-260816-005',
    srt: 'SRT-260815-005',
    status: 'CLM_COMPLETED',
    createdAt: '2026-08-15 09:30',
    operator: 'sari',
    picks: [
      { sj: '002/SJ/08/2026', sku: 'TBC-ONE-12', unit: 'Karton', n: 1, decision: [784, 0, 16], ngReason: 'Penyok' }
    ],
    store: { spc: 'SPC-260815-005', qty: 784, done: true }
  },
  {
    no: 'CLM-260818-006',
    srt: 'SRT-260818-011',
    status: 'CLM_SORTING',
    createdAt: '2026-08-18 12:00',
    operator: 'budi',
    picks: [
      { sj: '002/SJ/08/2026', sku: 'TBC-ONE-12', unit: 'Karton', n: 2 },
      { sj: '002/SJ/08/2026', sku: 'TBC-ONE-12', unit: 'Karton', n: 3 },
      { sj: '004/SJ/08/2026', sku: 'TBC-ONE-12', unit: 'Slop', n: 0 }
    ]
  },
  {
    no: 'CLM-260818-007',
    srt: 'SRT-260818-012',
    status: 'CLM_PENDING_SORT_APP',
    createdAt: '2026-08-18 13:00',
    operator: 'budi',
    agoMin: 3,
    picks: [{ sj: '001/SJ/08/2026', sku: 'TBC-BOLD-20', unit: 'Karton', n: 2, decision: 'GOOD' }]
  },
  {
    no: 'CLM-260818-008',
    srt: 'SRT-260818-008',
    status: 'CLM_PROCESSING',
    createdAt: '2026-08-18 09:30',
    operator: 'budi',
    picks: [
      {
        sj: '004/SJ/08/2026',
        sku: 'TBC-ONE-12',
        unit: 'Bal',
        n: 0,
        decision: [198, 1, 1],
        repackReason: 'Kemasan kotor',
        ngReason: 'Penyok'
      }
    ],
    putaway: { fpw: 'FPW-260818-031', ngNo: 'NGP-2608-006' }
  }
]

/* ------------------------------------------------------------ jalur status */

const PATH: Record<ClmStatusCode, ClmActionName[]> = {
  CLM_SORTING: [],
  CLM_PENDING_SORT_APP: ['ACT_CLM_FINISH_SORT'],
  CLM_SORT_APPROVED: ['ACT_CLM_FINISH_SORT', 'ACT_CLM_SPV_APPROVE'],
  CLM_PROCESSING: ['ACT_CLM_FINISH_SORT', 'ACT_CLM_SPV_APPROVE', 'ACT_CLM_PROCESS'],
  CLM_COMPLETED: ['ACT_CLM_FINISH_SORT', 'ACT_CLM_SPV_APPROVE', 'ACT_CLM_PROCESS', 'ACT_CLM_AUTO_COMPLETE']
}
const ACTOR: Partial<Record<ClmActionName, ClmActorCode>> = {
  ACT_CLM_FINISH_SORT: 'ACT_CLM_OP_FG',
  ACT_CLM_SPV_APPROVE: 'ACT_CLM_SPV_FG',
  ACT_CLM_PROCESS: 'ACT_CLM_ADMIN_FG'
}
const STEP_MINUTES: Record<ClmActionName, number> = {
  ACT_CLM_FINISH_SORT: 60,
  ACT_CLM_SPV_APPROVE: 80,
  ACT_CLM_PROCESS: 100,
  ACT_CLM_AUTO_COMPLETE: 300,
  ACT_CLM_RESORT: 70,
  ACT_CLM_AUTO_APPROVE: 80
}

const pad = (value: number, width = 2) => String(value).padStart(width, '0')
const shiftMinutes = (at: string, minutes: number) => {
  const date = new Date(utc(at))
  date.setMinutes(date.getMinutes() + minutes)
  const local = new Date(date.getTime() + 7 * 3600_000)
  return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`
}
const isoAgo = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString()

export function applyClaimData(seed: SeedTables): SeedTables {
  const products = new Map((seed.mt_product ?? []).map(row => [String(row.id), String(row.code)]))
  const plans = seed.delivery_plan ?? []
  const plansBySj = new Map(plans.map(row => [String(row.sj_no ?? ''), row]))
  const shipped = (seed.fg_label ?? []).filter(row => row.label_status === 'SHIPPED' && row.shipped_plan_id)
  const labelOfPick = (pick: Pick) => {
    const group = shipped
      .filter(row => {
        const plan = plans.find(item => item.id === row.shipped_plan_id)
        return plan?.sj_no === pick.sj && products.get(String(row.sku_id)) === pick.sku && row.level === pick.unit
      })
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    const label = group[pick.n]
    if (!label) throw new Error(`Seed claim: label ${pick.sj} ${pick.sku} ${pick.unit} #${pick.n} tidak ada`)
    return label
  }
  const packOf = (sku: string, unit: Pick['unit']) => {
    const per = packPer(sku)
    return unit === 'Karton' ? per.karton : unit === 'Bal' ? per.bal : per.slop
  }

  const claim: LocalRow[] = []
  const claim_item: LocalRow[] = []
  const claim_sort_task: LocalRow[] = []
  const claim_store_task: LocalRow[] = []
  const fg_ng_pack: LocalRow[] = []
  const fg_putaway_task: LocalRow[] = []
  const fg_putaway_task_item: LocalRow[] = []
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  const claimedLabels = new Set<string>()
  let logNo = 0
  let itemNo = 0
  let putawayItemNo = 0

  CLAIMS.forEach((spec, index) => {
    const n = index + 1
    const claimId = seedId(SEED_GROUP.claim, n)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 1300 + n)
    const taskId = seedId(SEED_GROUP.claimSortTask, n)
    const operatorId = USER_IDS[spec.operator]
    const path = PATH[spec.status]
    const stepAt = (action: ClmActionName) =>
      action === 'ACT_CLM_FINISH_SORT' && spec.agoMin !== undefined
        ? isoAgo(spec.agoMin)
        : utc(shiftMinutes(spec.createdAt, STEP_MINUTES[action]))
    const finished = path.includes('ACT_CLM_FINISH_SORT')
    const approved = path.includes('ACT_CLM_SPV_APPROVE')
    const processed = path.includes('ACT_CLM_PROCESS')
    const completed = path.includes('ACT_CLM_AUTO_COMPLETE')
    const labels = spec.picks.map(labelOfPick)
    const customers = labels.map(label =>
      String(plans.find(plan => plan.id === label.shipped_plan_id)?.customer_id ?? '')
    )

    claim.push({
      ...audit(ulil, spec.createdAt),
      id: claimId,
      clm_no: spec.no,
      customer_id: finished ? customers[0] || null : null,
      sj_list: finished ? [...new Set(spec.picks.map(pick => pick.sj))].join(', ') : null,
      status_id: WF_CLM.statusId(spec.status),
      submission_id: submissionId,
      lot_no: `LOT-${spec.no}`,
      sort_locked_at: finished ? stepAt('ACT_CLM_FINISH_SORT') : null,
      d2_decided_by_id: approved ? hendra : null,
      d2_decided_at: approved ? stepAt('ACT_CLM_SPV_APPROVE') : null,
      d2_auto: false,
      return_reason: null,
      resort_count: 0,
      processed_at: processed ? stepAt('ACT_CLM_PROCESS') : null,
      processed_by_id: processed ? ulil : null,
      is_completed: completed,
      completed_at: completed ? stepAt('ACT_CLM_AUTO_COMPLETE') : null
    })
    wxl_submission.push({
      ...audit(ulil, spec.createdAt),
      id: submissionId,
      workflow_id: WF_CLM.workflowId,
      entity: 'claim',
      entity_id: claimId,
      source: 'ACTION',
      current_status_id: WF_CLM.statusId(spec.status),
      last_action_id: path.length ? WF_CLM.actionId(path[path.length - 1]) : null,
      workflow_snapshot: null
    })
    path.forEach(action => {
      const [from, to] = WF_CLM.actionStatus[action]
      const actor = ACTOR[action]
      logNo += 1
      wxl_submission_log.push({
        ...audit(
          actor === 'ACT_CLM_OP_FG' ? operatorId : actor === 'ACT_CLM_SPV_FG' ? hendra : actor ? ulil : null,
          spec.createdAt
        ),
        date_created: stepAt(action),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 40000 + logNo),
        workflow_id: WF_CLM.workflowId,
        submission_id: submissionId,
        action_id: WF_CLM.actionId(action),
        actor_id: actor ? WF_CLM.actorId(actor) : null,
        from_status_id: WF_CLM.statusId(from),
        to_status_id: WF_CLM.statusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason:
          action === 'ACT_CLM_AUTO_COMPLETE'
            ? 'Seluruh barang claim sudah tersimpan — claim selesai otomatis'
            : action === 'ACT_CLM_PROCESS' && spec.store
              ? 'Tugas simpan (alur claim lama)'
              : null,
        entity: 'claim',
        entity_id: claimId
      })
    })

    claim_sort_task.push({
      ...audit(ulil, spec.createdAt),
      id: taskId,
      bkr_no: spec.srt,
      claim_id: claimId,
      operator_id: operatorId,
      operator_parts: [
        { operator_id: operatorId, part_name: null, done_at: finished ? stepAt('ACT_CLM_FINISH_SORT') : null }
      ],
      task_status: finished ? 'DONE' : 'ASSIGNED',
      locked_at: finished ? stepAt('ACT_CLM_FINISH_SORT') : null
    })

    const itemIds: string[] = []
    spec.picks.forEach((pick, pickIndex) => {
      itemNo += 1
      const label = labels[pickIndex]
      const plan = plansBySj.get(pick.sj)
      claimedLabels.add(String(label.id))
      const eq = packOf(pick.sku, pick.unit)
      const split = pick.decision === 'GOOD' ? [eq, 0, 0] : pick.decision
      const itemId = seedId(SEED_GROUP.claimItem, itemNo)
      itemIds.push(itemId)
      const scannedAt = shiftMinutes(spec.createdAt, 10 + pickIndex)
      claim_item.push({
        ...audit(operatorId, scannedAt),
        id: itemId,
        claim_id: claimId,
        label_id: label.id,
        equivalent_pack: eq,
        sort_task_id: taskId,
        sort_result: pick.decision === 'GOOD' ? 'GOOD_INTACT' : pick.decision ? 'OPENED' : null,
        qty_good: split?.[0] ?? null,
        qty_repack: split?.[1] ?? null,
        qty_ng: split?.[2] ?? null,
        repack_reason_id: pick.repackReason ? claimReasonId('FG_REPACK', pick.repackReason) : null,
        reject_reason_id: pick.ngReason ? claimReasonId('FG_NG', pick.ngReason) : null,
        sj_no: pick.sj,
        shipped_at: plan?.shipped_at ?? null,
        order_no: plan?.plan_no ?? null,
        customer_id: customers[pickIndex] || null,
        scanned_by_id: operatorId,
        sorted_at: pick.decision ? utc(shiftMinutes(spec.createdAt, 30 + pickIndex)) : null,
        sorted_by_id: pick.decision ? operatorId : null
      })
    })

    if (spec.store) {
      claim_store_task.push({
        ...audit(ulil, shiftMinutes(spec.createdAt, 100)),
        id: seedId(SEED_GROUP.claimStoreTask, n),
        spc_no: spec.store.spc,
        claim_id: claimId,
        category: 'GOOD',
        source: 'LEGACY',
        target_pallet_id: claimPalletId('RC-P1'),
        from_pallet_id: null,
        ng_pack_ids: null,
        operator_id: operatorId,
        qty_packs: spec.store.qty,
        deviation_reason: null,
        task_status: spec.store.done ? 'DONE' : 'ASSIGNED',
        stored_packs: spec.store.done ? spec.store.qty : null,
        done_at: spec.store.done ? utc(shiftMinutes(spec.createdAt, 280)) : null
      })
    }

    if (spec.putaway) {
      const pick = spec.picks[0]
      const label = labels[0]
      const [good, repack, ng] = pick.decision as [number, number, number]
      const putawayId = seedId(SEED_GROUP.fgPutawayTask, 900 + n)
      const ngPackId = seedId(SEED_GROUP.fgNgPack, 50 + n)
      const lotTail = spec.no
      const processedAt = shiftMinutes(spec.createdAt, STEP_MINUTES.ACT_CLM_PROCESS)
      fg_putaway_task.push({
        ...audit(ulil, processedAt),
        id: putawayId,
        fpw_no: spec.putaway.fpw,
        batch_id: null,
        sku_id: label.sku_id,
        source_type: 'CLAIM',
        claim_id: claimId,
        operator_id: operatorId,
        task_status: 'ASSIGNED',
        source_ref: spec.no,
        note: 'Claim'
      })
      fg_ng_pack.push({
        ...audit(ulil, processedAt),
        id: ngPackId,
        ng_no: spec.putaway.ngNo,
        claim_id: claimId,
        claim_item_id: itemIds[0],
        pallet_id: null,
        qty_pack: ng,
        reason_id: pick.ngReason ? claimReasonId('FG_NG', pick.ngReason) : null,
        destruction_id: null,
        ng_status: 'AWAITING_STORE',
        queued_at: null,
        current_area: null,
        destroyed_at: null,
        parent_pack_id: null,
        note: null
      })
      const containerPack = (seed.container ?? []).find(row => row.container_type === 'Pack')
      const rows: [string, number, string, boolean, string | null, string | null][] = [
        ['GOOD', good, `PK-${lotTail}-G01`, false, null, String(containerPack?.id ?? '')],
        ['REPACK', repack, `PK-${lotTail}-R01`, true, claimPalletId('RC-P2'), null],
        ['NG', ng, spec.putaway.ngNo, true, claimPalletId('ZN-01-P1'), null]
      ]
      rows.forEach(([category, qty, code, locked, pallet, container]) => {
        putawayItemNo += 1
        fg_putaway_task_item.push({
          ...audit(ulil, processedAt),
          id: seedId(SEED_GROUP.fgPutawayItem, 9000 + putawayItemNo),
          task_id: putawayId,
          unit_type: 'Pack',
          target_pallet_id: pallet,
          target_container_id: container || null,
          qty,
          override_reason: null,
          label_id: null,
          label_no: code,
          batch_id: label.batch_id ?? null,
          category,
          is_target_locked: locked,
          ng_pack_id: category === 'NG' ? ngPackId : null,
          source_item_ids: [itemIds[0]],
          done_qty: 0,
          actual_pallet_id: null,
          actual_container_id: null,
          item_status: 'PENDING'
        })
      })
    }
  })

  const labels = (seed.fg_label ?? []).map(row =>
    claimedLabels.has(String(row.id)) ? { ...row, label_status: 'CLAIM_RECEIVED' } : row
  )

  return {
    ...seed,
    fg_label: labels,
    warehouse_zone: [
      ...(seed.warehouse_zone ?? []),
      ...CLAIM_ZONES.map(([code, name, type]) => ({
        ...audit(ulil, '2026-08-01 07:00'),
        id: claimZoneId(code),
        code,
        name,
        warehouse_id: FG_WAREHOUSE_ID,
        zone_type: type
      }))
    ],
    pallet: [
      ...(seed.pallet ?? []),
      ...CLAIM_PALLETS.map(([zone, code]) => ({
        ...audit(ulil, '2026-08-01 07:00'),
        id: claimPalletId(code),
        code,
        pallet_kind: 'FG',
        zone_id: claimZoneId(zone),
        capacity: 24,
        recommended: 18,
        unit_type: null,
        description: zone === 'RC' ? `Pallet Zone Retur Claim ${code}` : `Pallet Area NG FG ${code}`,
        pallet_status: 'EMPTY',
        cycle_count: 0,
        is_active: true,
        sku_id: null,
        occupied: 0
      }))
    ],
    mt_reject_type: [
      ...(seed.mt_reject_type ?? []),
      ...REASON_LIST.map(([category, name], index) => ({
        ...audit(ulil, '2026-08-01 07:00'),
        id: seedId(SEED_GROUP.rejectType, 100 + index),
        code: `${category}-${pad(index + 1, 2)}`,
        name,
        description: null,
        categories: [category],
        sort: index + 1,
        is_active: true
      }))
    ],
    claim,
    claim_item,
    claim_sort_task,
    claim_store_task,
    fg_ng_pack,
    fg_putaway_task: [...(seed.fg_putaway_task ?? []), ...fg_putaway_task],
    fg_putaway_task_item: [...(seed.fg_putaway_task_item ?? []), ...fg_putaway_task_item],
    wxl_submission: [...(seed.wxl_submission ?? []), ...wxl_submission],
    wxl_submission_log: [...(seed.wxl_submission_log ?? []), ...wxl_submission_log]
  }
}
